import { cn } from "@/lib/utils";
import type { ReactNode, CSSProperties } from "react";

interface GlassmorphicCardProps {
    children: ReactNode;
    className?: string;
    /** Gradient background on hover */
    hoverGradient?: string;
    /** Enable animated gradient border */
    animatedBorder?: boolean;
    /** Card padding preset */
    padding?: "none" | "sm" | "md" | "lg";
    /** Glow color on hover */
    glowColor?: string;
    /** Custom inline styles */
    style?: CSSProperties;
}

/**
 * GlassmorphicCard - Modern glassmorphism card with hover effects
 * Features: blur backdrop, gradient borders, glow effects, shimmer animation
 */
export const GlassmorphicCard = ({
    children,
    className,
    hoverGradient = "from-primary/5 via-transparent to-secondary/5",
    animatedBorder = false,
    padding = "md",
    glowColor = "primary",
    style,
}: GlassmorphicCardProps) => {
    const paddingClasses = {
        none: "",
        sm: "p-4",
        md: "p-6 md:p-8",
        lg: "p-8 md:p-12",
    };

    const glowClasses: Record<string, string> = {
        primary: "shadow-primary/20 hover:shadow-primary/30",
        secondary: "shadow-secondary/20 hover:shadow-secondary/30",
        violet: "shadow-blue-500/20 hover:shadow-blue-500/30",
        rose: "shadow-rose-500/20 hover:shadow-rose-500/30",
        blue: "shadow-blue-500/20 hover:shadow-blue-500/30",
        cyan: "shadow-blue-500/20 hover:shadow-blue-500/30",
    };

    return (
        <div
            className={cn(
                // Base structure
                "group relative overflow-hidden rounded-2xl md:rounded-3xl",
                // Glassmorphism
                "bg-card/40 backdrop-blur-xl",
                // Border
                "border border-border/30 hover:border-primary/40",
                // Shadow with glow
                "shadow-xl hover:shadow-2xl",
                glowClasses[glowColor] || glowClasses.primary,
                // Transitions
                "transition-all duration-500 ease-out",
                // Hover lift
                "hover:-translate-y-1",
                // Padding
                paddingClasses[padding],
                className
            )}
            style={style}
        >
            {/* Gradient overlay on hover */}
            <div
                className={cn(
                    "absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none",
                    `bg-gradient-to-br ${hoverGradient}`
                )}
            />

            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out pointer-events-none" />

            {/* Animated gradient border */}
            {animatedBorder && (
                <div className="absolute inset-0 rounded-2xl md:rounded-3xl overflow-hidden pointer-events-none">
                    <div className="absolute inset-[-2px] bg-gradient-to-r from-primary via-secondary via-blue-500 to-primary bg-[length:400%_100%] animate-gradient-flow opacity-0 group-hover:opacity-30 transition-opacity duration-500 blur-[1px]" />
                </div>
            )}

            {/* Inner glow on hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-1/2 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10">{children}</div>
        </div>
    );
};

/**
 * FloatingShapes - Decorative background elements for visual interest
 */
export const FloatingShapes = ({ className }: { className?: string }) => {
    return (
        <div className={cn("absolute inset-0 pointer-events-none overflow-hidden", className)}>
            {/* Primary orb - top right */}
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] animate-mesh-1">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-blue-500/15 to-transparent rounded-full blur-[100px]" />
            </div>

            {/* Secondary orb - bottom left */}
            <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] animate-mesh-2">
                <div className="absolute inset-0 bg-gradient-to-tr from-secondary/20 via-amber-500/15 to-transparent rounded-full blur-[80px]" />
            </div>

            {/* Accent orb - center right */}
            <div className="absolute top-1/2 -right-20 w-[300px] h-[300px] animate-mesh-3">
                <div className="absolute inset-0 bg-gradient-to-l from-accent/15 via-amber-500/10 to-transparent rounded-full blur-[60px]" />
            </div>

            {/* Floating particles */}
            <div className="absolute inset-0">
                {[...Array(12)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-1 h-1 bg-primary/20 rounded-full animate-particle"
                        style={{
                            left: `${10 + (i * 7) % 80}%`,
                            top: `${(i * 13) % 100}%`,
                            animationDelay: `${i * 2}s`,
                            animationDuration: `${20 + (i % 5) * 5}s`,
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

/**
 * SectionWrapper - Consistent section styling with scroll animations
 */
export const SectionWrapper = ({
    children,
    className,
    id,
}: {
    children: ReactNode;
    className?: string;
    id?: string;
}) => {
    return (
        <section
            id={id}
            className={cn(
                "relative py-20 md:py-32",
                className
            )}
        >
            {children}
        </section>
    );
};

/**
 * GradientText - Animated gradient text
 */
export const GradientText = ({
    children,
    className,
    animate = true,
}: {
    children: ReactNode;
    className?: string;
    animate?: boolean;
}) => {
    return (
        <span
            className={cn(
                "bg-gradient-to-r from-primary via-blue-500 to-secondary bg-clip-text text-transparent",
                animate && "bg-[length:200%_auto] animate-gradient-flow",
                className
            )}
        >
            {children}
        </span>
    );
};

export default GlassmorphicCard;
